import * as React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import MainAppPanel, {
  LOGIN_URL,
  PO_URL,
  PO_VENDORS_URL,
  RFQ_URL,
  QUEUE_URL,
  ZENKIT_URL,
  QB_ITEMS_URL,
  ROCKET_COMPARE_URL,
  PO_ITEMS_URL,
  ALL_ITEMS_URL,
  ORDERS_URL,
  VENDOR_ORDERS_URL,
  COMPLETED_POS_URL,
  PRICE_QUOTES_URL,
  PRICE_QUOTE_REVIEW_URL,
  PO_REVIEW_URL,
  RFQ_HISTORY_URL,
  SAM_GOV_OPPORTUNITIES_URL,
  ADMIN_FEES_URL,
  RFQ_TIMING_URL,
  PROOF_OF_DELIVERY_URL,
  PROOF_OF_DELIVERY_NEW_URL,
  PROOF_OF_DELIVERY_CAPTURE_URL,
  PROOF_OF_DELIVERY_DETAIL_URL,
} from "./MainAppPanel";
import { CHAT_URL } from "./components/chat/chatRoutes";
import { useContext } from "react";
import { AppContext } from "./AppContextProvider";
import LoginPanel from "./components/users/LoginPanel";
import { Role } from "./models/User";
import InvitationPanel from "./components/users/InvitationPanel";
import RegistrationPanel from "./components/users/RegistrationPanel";
import ChangeForgottenPwdPanel from "./components/users/ChangeForgottenPwdPanel";
import PageNotFound from "./components/PageNotFound";
import ChangePasswordPage from "./components/settings/ChangePasswordPage";
import RfqsPage from "./components/rfqs/RfqsPage";

const UsersPage = React.lazy(() => import("./components/users/UsersPage"));
const TaxCertificatePage = React.lazy(
  () => import("./components/settings/TaxCertificatePage")
);
const CustomersPage = React.lazy(
  () => import("./components/customers/CustomersPage")
);
const BuyersPage = React.lazy(
  () => import("./components/customers/BuyersPage")
);
const VendorsPage = React.lazy(
  () => import("./components/vendors/VendorsPage")
);
const QCCodesPage = React.lazy(
  () => import("./components/qcCodes/QCCodesPage")
);
const AdminFeeReportPage = React.lazy(
  () => import("./components/settings/AdminFeeReportPage")
);
const RfqTimingReportPage = React.lazy(
  () => import("./components/settings/RfqTimingReportPage")
);
const FeeTypesPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.FeeTypesPage,
  }))
);
const NoteCategoriesPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.NoteCategoriesPage,
  }))
);
const PartNumberTypesPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.PartNumberTypesPage,
  }))
);
const DeliveryMethodsPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.DeliveryMethodsPage,
  }))
);
const DeliverySpeedsPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.DeliverySpeedsPage,
  }))
);
const RfqStatusesPage = React.lazy(() =>
  import("./components/catalogTypes/CatalogTypesPage").then((m) => ({
    default: m.RfqStatusesPage,
  }))
);
const PurchaseOrderPanel = React.lazy(
  () => import("./components/rfqs/PurchaseOrderPanel")
);
const VendorOrdersPanel = React.lazy(
  () => import("./components/rfqs/VendorOrdersPanel")
);
const PurchaseOrdersItemsPanel = React.lazy(
  () => import("./components/rfqs/PurchaseOrderItemsPanel")
);
const VendorOrdersPage = React.lazy(
  () => import("./components/rfqs/VendorOrdersPage")
);
const CompletedPOsPage = React.lazy(
  () => import("./components/rfqs/CompletedPOsPage")
);
const PriceQuotesPage = React.lazy(
  () => import("./components/rfqs/PriceQuotesPage")
);
const PriceQuoteReviewPage = React.lazy(
  () => import("./components/rfqs/PriceQuoteReviewPage")
);
const PurchaseOrderReviewPage = React.lazy(
  () => import("./components/rfqs/PurchaseOrderReviewPage")
);
const ProductDetailsPanel = React.lazy(
  () => import("./components/rfqs/ProductDetailsPanel")
);
const RFQHistoryTable = React.lazy(
  () => import("./components/rfqs/RFQHistoryTable")
);
const EmailInbox = React.lazy(
  () => import("./components/emails/EmailInbox")
);
const RfqPanel = React.lazy(() => import("./components/rfqs/RfqPanel"));
const ZenkitQueuePage = React.lazy(
  () => import("./components/rfqs/ZenkitQueuePage")
);
const ZenkitDetailPage = React.lazy(
  () => import("./components/rfqs/zenkit/ZenkitDetailPage")
);
const QbItemsPage = React.lazy(() => import("./components/rfqs/QbItemsPage"));
const RocketComparePage = React.lazy(
  () => import("./components/rfqs/RocketComparePage")
);
const ProofOfDeliveryPage = React.lazy(
  () => import("./components/pod/ProofOfDeliveryPage")
);
const PodReceiptsListPage = React.lazy(
  () => import("./components/pod/PodReceiptsListPage")
);
const PodBatchCapturePage = React.lazy(
  () => import("./components/pod/PodBatchCapturePage")
);
const PodReceiptDetailPage = React.lazy(
  () => import("./components/pod/PodReceiptDetailPage")
);
const SamGovOpportunitiesPage = React.lazy(
  () => import("./components/rfqs/SamGovOpportunitiesPage")
);
const ChatWorkspace = React.lazy(
  () => import("./components/chat/ChatWorkspace")
);

class RouteErrorBoundary extends React.Component<
  { label: string; resetKey?: string; children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`${this.props.label} crashed:`, error, info.componentStack);
  }

  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          {this.props.label} hit an error
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", mb: 2 }}
        >
          {error.message || String(error)}
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Box>
      </Box>
    );
  }
}

function ZenkitErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary label="Zenkit" resetKey={location.pathname}>
      {children}
    </RouteErrorBoundary>
  );
}

function RouteFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240,
        width: "100%",
      }}
    >
      <CircularProgress size={28} />
    </Box>
  );
}

const App: React.FC = () => {
  const { user } = useContext(AppContext);

  const defaultUrl = QUEUE_URL;

  return (
    <BrowserRouter>
      <React.Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/invite/:inviteId" element={<InvitationPanel />} />
          <Route path="/register/:inviteId" element={<RegistrationPanel />} />
          <Route
            path="/login/:inviteId"
            element={
              user.id ? <Navigate to={defaultUrl} replace /> : <LoginPanel />
            }
          />

          <Route path="/productdetails" element={<ProductDetailsPanel />} />

          <Route
            path="/login"
            element={
              user.id ? <Navigate to={defaultUrl} replace /> : <LoginPanel />
            }
          />
          <Route
            path="/forgot/:forgottenLoginId"
            element={
              user.id ? (
                <Navigate to={defaultUrl} replace />
              ) : (
                <ChangeForgottenPwdPanel />
              )
            }
          />

          <Route
            path="/"
            element={
              !user.id ? <Navigate to={LOGIN_URL} replace /> : <MainAppPanel />
            }
          >
            <Route path="account" element={<ChangePasswordPage />} />
            <Route
              path={QUEUE_URL.substring(1)}
              element={<RfqsPage />}
            />
            <Route
              path={ZENKIT_URL.substring(1)}
              element={
                <ZenkitErrorBoundary>
                  <ZenkitQueuePage />
                </ZenkitErrorBoundary>
              }
            >
              <Route path="v/:viewId" element={null} />
            </Route>
            <Route
              path={`${ZENKIT_URL.substring(1)}/q/:queueId`}
              element={
                <ZenkitErrorBoundary>
                  <ZenkitDetailPage />
                </ZenkitErrorBoundary>
              }
            />
            <Route path={QB_ITEMS_URL.substring(1)} element={<QbItemsPage />} />
            <Route
              path={ROCKET_COMPARE_URL.substring(1)}
              element={<RocketComparePage />}
            />
            <Route
              path={PROOF_OF_DELIVERY_URL.substring(1)}
              element={<PodReceiptsListPage />}
            />
            <Route
              path={PROOF_OF_DELIVERY_NEW_URL.substring(1)}
              element={<ProofOfDeliveryPage />}
            />
            <Route
              path={PROOF_OF_DELIVERY_CAPTURE_URL.substring(1)}
              element={<PodBatchCapturePage />}
            />
            <Route
              path={PROOF_OF_DELIVERY_DETAIL_URL.substring(1)}
              element={<PodReceiptDetailPage />}
            />
            <Route
              path="emails/:emailId?"
              element={<EmailInbox />}
            />
            <Route path={RFQ_URL.substring(1)} element={<RfqPanel />} />
            <Route
              path={RFQ_HISTORY_URL.substring(1)}
              element={<RFQHistoryTable />}
            />
            <Route
              path={ALL_ITEMS_URL.substring(1)}
              element={<Navigate to={COMPLETED_POS_URL} replace />}
            />
            <Route
              path={COMPLETED_POS_URL.substring(1)}
              element={<CompletedPOsPage />}
            />
            <Route
              path={SAM_GOV_OPPORTUNITIES_URL.substring(1)}
              element={<SamGovOpportunitiesPage />}
            />
            <Route
              path={ORDERS_URL.substring(1)}
              element={<VendorOrdersPage />}
            />
            <Route
              path={VENDOR_ORDERS_URL.substring(1)}
              element={<VendorOrdersPage />}
            />
            <Route
              path={PRICE_QUOTES_URL.substring(1)}
              element={<PriceQuotesPage />}
            />
            <Route
              path={PRICE_QUOTE_REVIEW_URL.substring(1)}
              element={<PriceQuoteReviewPage />}
            />
            <Route
              path={PO_REVIEW_URL.substring(1)}
              element={<PurchaseOrderReviewPage />}
            />
            <Route path={PO_URL.substring(1)} element={<PurchaseOrderPanel />} />
            <Route
              path={PO_VENDORS_URL.substring(1)}
              element={<VendorOrdersPanel />}
            />
            <Route
              path={PO_ITEMS_URL.substring(1)}
              element={<PurchaseOrdersItemsPanel />}
            />
            {user.role !== Role.CUSTOMER ? (
              <>
                <Route path="customers" element={<CustomersPage />} />
                <Route path="vendors" element={<VendorsPage />} />
                <Route path="qc-codes" element={<QCCodesPage />} />
                <Route
                  path={`${CHAT_URL.substring(1)}/:conversationId?/:threadId?`}
                  element={<ChatWorkspace />}
                />
              </>
            ) : (
              <></>
            )}
            {user.role === Role.ADMIN ? (
              <>
                <Route path="users" element={<UsersPage />} />
                <Route path="buyers" element={<BuyersPage />} />
                <Route
                  path="part-number-types"
                  element={<PartNumberTypesPage />}
                />
                <Route path="fee-types" element={<FeeTypesPage />} />
                <Route
                  path="note-categories"
                  element={<NoteCategoriesPage />}
                />
                <Route
                  path="delivery-methods"
                  element={<DeliveryMethodsPage />}
                />
                <Route
                  path="delivery-speeds"
                  element={<DeliverySpeedsPage />}
                />
                <Route path="rfq-statuses" element={<RfqStatusesPage />} />
                <Route
                  path="tax-certificate"
                  element={<TaxCertificatePage />}
                />
                <Route
                  path={ADMIN_FEES_URL.substring(1)}
                  element={<AdminFeeReportPage />}
                />
                <Route
                  path={RFQ_TIMING_URL.substring(1)}
                  element={<RfqTimingReportPage />}
                />
              </>
            ) : (
              <></>
            )}
          </Route>
          <Route path="/404" element={<PageNotFound />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
};

export default App;
